import React from 'react'
import { motion } from 'framer-motion'

export interface TotalProjectsBarData {
  month: string // Short month name: 'Oct', 'Nov', etc.
  value: number // Value for relative positioning (0-100 or actual value)
  isHighlighted?: boolean // If true, uses yellow color and higher position, otherwise gray
}

interface TotalProjectsGraphProps {
  data?: TotalProjectsBarData[]
  className?: string
}

// Default data for demonstration
const DEFAULT_DATA: TotalProjectsBarData[] = [
  { month: 'Oct', value: 60, isHighlighted: false },
  { month: 'Nov', value: 40, isHighlighted: false },
  { month: 'Dec', value: 100, isHighlighted: true },
]

const TotalProjectsGraph: React.FC<TotalProjectsGraphProps> = ({ 
  data = DEFAULT_DATA,
  className = ''
}) => {
  // Calculate positions based on value (normalized to 0-100)
  const maxValue = Math.max(...data.map(d => d.value))
  const minValue = Math.min(...data.map(d => d.value))
  const range = maxValue - minValue || 1

  return (
    <div 
      className={`relative w-full max-w-[214px] h-[80px] ${className}`}
    >
      {data.map((item, index) => {
        const isHighlighted = item.isHighlighted ?? false
        
        // Calculate positions based on value relative to other values
        // Higher value = higher position (closer to top)
        const normalizedValue = range > 0 
          ? ((item.value - minValue) / range) 
          : 0.5
        
        // Position from top: scale from 22px (highest) to 50px (lowest) - reduced by 20%
        // Dec (highlighted) should be at top (22px), others scaled
        const topPosition = isHighlighted 
          ? 22 
          : 38 + (normalizedValue * 12) // Range: 38-50px for non-highlighted
        
        // Gradient height: scale from 30px (lowest) to 58px (highest) - reduced by 20%
        const gradientHeight = isHighlighted 
          ? 58 
          : 30 + (normalizedValue * 12) // Range: 30-42px for non-highlighted
        
        return (
          <div
            key={`${item.month}-${index}`}
            className="absolute h-full w-[72px]"
            style={{
              left: `${index * 72}px`,
            }}
          >
            {/* Dashed border - separate div for dark mode support */}
            <div 
              className="absolute inset-0 border-l border-dashed border-[rgba(45,41,35,0.32)] dark:border-[rgba(45,41,35,0.32)]"
              style={{
                borderLeftWidth: index === 0 ? '0' : '1px',
              }}
            />
            
            {/* Gradient tail */}
            <motion.div
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: gradientHeight, opacity: 1 }}
              transition={{
                duration: 0.6,
                delay: index * 0.1,
                ease: [0.16, 1, 0.3, 1]
              }}
              className={`absolute left-[3px] rounded-t-sm w-16 ${
                isHighlighted 
                  ? 'bg-gradient-to-b from-[rgba(227,253,90,0.28)] to-transparent' 
                  : 'bg-gradient-to-b from-[#f7f7f7] to-transparent dark:from-[rgba(177,176,176,0.3)] dark:to-transparent'
              }`}
              style={{
                top: `${topPosition}px`,
                height: `${gradientHeight}px`,
              }}
            />
            
            {/* Bar */}
            <motion.div
              initial={{ opacity: 0, scaleX: 0 }}
              animate={{ opacity: 1, scaleX: 1 }}
              transition={{
                duration: 0.4,
                delay: index * 0.1 + 0.2,
                ease: [0.16, 1, 0.3, 1]
              }}
              className={`absolute left-[3px] rounded-lg w-16 h-1 origin-left ${
                isHighlighted 
                  ? 'bg-[#dffb2c]' 
                  : 'bg-[#f5f5f5] dark:bg-[#b1b0b0]'
              }`}
              style={{
                top: `${topPosition}px`,
              }}
            />
            
            {/* Month label */}
            <span
              className={`absolute left-1/2 -translate-x-1/2 text-center text-xs leading-4 font-normal w-16 ${
                isHighlighted 
                  ? 'text-[#2d2923] dark:text-[#fdfdfd]' 
                  : 'text-[rgba(45,41,35,0.72)] dark:text-[rgba(253,253,253,0.72)]'
              }`}
              style={{
                top: `${topPosition + 8}px`,
              }}
            >
              {item.month}
            </span>
          </div>
        )
      })}
    </div>
  )
}

export default TotalProjectsGraph

